import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import {} from 'react-select';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { memoGeneric } from '@/js/libs/memo/index.ts';
import { SelectionList, SortOrder, ITEM_MIN_HEIGHT_WITH_BORDER, keepAfterBulkRemove, } from '../../Components/SelectionList/index.js';
import { Icons } from '../../index.js';
import { MultiSelect } from '../Select/index.js';
import { SelectListWrapper } from './styles.js';
import {} from './types.js';
const ITEM_HEIGHT = ITEM_MIN_HEIGHT_WITH_BORDER;
export const SelectList = memoGeneric(function SelectList({ dataTestId, options, value = [], onChange, initialSelectedSortOrder = SortOrder.ASC, translations, visibleRowsCount = 4, placeholder, isLoading, isDisabled, getOptionKey, getOptionValue, formatOptionLabel, getOptionText, error, hideEmptySelectedList = false, }) {
    const [selectedSortOrder, setSelectedSortOrder] = useState(initialSelectedSortOrder);
    const getSortLabel = useCallbackRef((option) => {
        if (getOptionText) {
            return getOptionText(option);
        }
        return formatOptionLabel?.(option) ?? option.label;
    });
    const selectedOptions = useMemo(() => value
        .map((option) => ({ ...option, sortLabel: getSortLabel(option) }))
        .sort((a, b) => selectedSortOrder === SortOrder.ASC ? a.sortLabel.localeCompare(b.sortLabel) : b.sortLabel.localeCompare(a.sortLabel)), [value, selectedSortOrder, getSortLabel]);
    const handleClear = useCallbackRef(() => {
        // Clearing removes everything the list is allowed to remove, which spares the fixed options — the
        // same contract `MultiSelection` and `useFixedOptions` already keep for the flag.
        onChange?.(keepAfterBulkRemove(value, value), { action: 'clear', removedValues: value.filter((option) => !option.fixed) });
    });
    const handleRemoveOption = useCallbackRef((option) => {
        const filteredValue = getOptionValue
            ? value.filter((v) => getOptionValue(v) !== getOptionValue(option))
            : value.filter((v) => v.value !== option.value);
        onChange?.(filteredValue, {
            action: 'deselect-option',
            removedValues: [option],
            option,
        });
    });
    const toggleSortOrder = useCallbackRef(() => {
        setSelectedSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
    });
    const listTranslations = useMemo(() => ({
        header: translations.selected,
        footer: `${selectedOptions.length} ${translations.totalSeparator} ${options?.length ?? 0}`,
        bulkActionTooltip: translations.clearAll,
        noEntries: translations.noEntries,
    }), [options?.length, selectedOptions.length, translations]);
    return (_jsxs(SelectListWrapper, { "data-testid": dataTestId, children: [_jsx(MultiSelect, { controlShouldRenderValue: false, isClearable: false, isDisabled: isDisabled || isLoading, options: options, value: value, onChange: onChange, getOptionValue: getOptionValue, formatOptionLabel: formatOptionLabel, placeholder: placeholder }), (!hideEmptySelectedList || selectedOptions.length > 0) && (_jsx(SelectionList, { dataTestId: `${dataTestId}-selection-list`, isLoading: isLoading, disabled: isDisabled || !selectedOptions.length, options: selectedOptions, onRowAction: handleRemoveOption, onBulkAction: handleClear, sortOrder: selectedSortOrder, onChangeSortOrder: toggleSortOrder, containerHeight: Math.max(visibleRowsCount, 1) * ITEM_HEIGHT + ITEM_HEIGHT / 2, getOptionKey: getOptionKey, formatOptionLabel: formatOptionLabel, translations: listTranslations, bulkActionIcon: _jsx(Icons.Clear, {}), rowActionIcon: _jsx(Icons.Remove, {}), error: error }))] }));
});
